/**
 * Pannello di configurazione del tiro, per l'istruttore di tiro.
 *
 * È un pannello admin **ridotto alla sua disciplina**: division, classi di
 * merito e sistemi di punteggio. Non utenti, non team, non programmi, non le
 * discipline degli altri.
 *
 * Perché non dargli l'admin pieno: con un click di troppo si spegne un coach o
 * si riscrivono le tabelle del CrossFit. Il permesso è
 * `reference_admin_disciplines` — una **lista**, non un flag, perché quando il
 * progetto si aprirà ad altri istruttori ognuno dovrà amministrare la propria
 * federazione senza toccare quella del vicino.
 *
 * Il confine è imposto dal backend (`services/reference_admin.py`): questa
 * pagina lo rispecchia, non lo definisce.
 */
import { Navigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { BarChart3, BookOpen } from "lucide-react";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { ShootingReferenceTab } from "../components/admin/ShootingReferenceTab";
import { Card, CardContent } from "../components/ui/card";

export default function ShootingAdmin() {
	const { user, loading } = useAuth();

	if (loading) return null;
	if (!user) return <Navigate to="/login" replace />;

	const managed = user.reference_admin_disciplines || [];
	if (!user.is_admin && managed.length === 0) {
		return <Navigate to="/dashboard" replace />;
	}

	return (
		<Layout>
			<div className="max-w-5xl mx-auto px-4 py-5 pb-28 space-y-4">
				<div>
					<h1 className="font-heading text-2xl">Configurazione tiro</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Division, classi di merito e sistemi di punteggio
						{managed.length > 0 && !user.is_admin
							? ` — discipline che amministri: ${managed.join(", ")}`
							: ""}
						.
					</p>
				</div>

				{/* Scorciatoie verso le altre due cose che servono a chiudere il
				    giro: i drill da censire e le tabelle del profilo prestativo. */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
					<Link to="/shooting-drills">
						<Card className="bg-card border-border rounded-sm hover:border-volt/40 transition-colors">
							<CardContent className="p-4 flex items-center gap-3">
								<BookOpen className="w-5 h-5 text-volt shrink-0" />
								<div className="min-w-0">
									<p className="font-medium text-sm">Libreria drill</p>
									<p className="text-xs text-muted-foreground">
										Censisci i drill con video, descrizione e piantina
									</p>
								</div>
							</CardContent>
						</Card>
					</Link>
					{/* I modelli prestativi stanno nella sezione coach «Sport e modelli».
					    L'admin di una disciplina legge li' il modello di sistema e si
					    crea il suo: modificarlo e' solo dell'admin (D17 di docs/sport-config). */}
					<Link to="/sport-config">
						<Card className="bg-card border-border rounded-sm hover:border-volt/40 transition-colors">
							<CardContent className="p-4 flex items-center gap-3">
								<BarChart3 className="w-5 h-5 text-volt shrink-0" />
								<div className="min-w-0">
									<p className="font-medium text-sm">Profilo prestativo</p>
									<p className="text-xs text-muted-foreground">
										Domini, benchmark e tabelle di riferimento
									</p>
								</div>
							</CardContent>
						</Card>
					</Link>
				</div>

				<ShootingReferenceTab />
			</div>
		</Layout>
	);
}
